// Unpublished Work © 2023-2024 Deere & Company.
import { AxiosResponse } from "axios";
import {
  ALL_ACCOUNTS,
  INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION,
  INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION,
  INVOICE_CONSOLIDATION,
  INVOICE_EXCEL,
  INVOICE_FILTER,
  RETURN_SUMMARY_EXCEL,
  RETURNS_EXCEL,
} from "../../utils/constants";
import axios from "../api";

import {
  AllOrdersExcelDownloadInvokeRequest,
  AllOrdersExcelDownloadWithFiltersInvokeRequest,
  IInvoiceExcelDownloadJobRegistrationRequest,
  IInvoiceExcelDownloadJobRegistrationRequestV2,
  IJobRegistrationResponse,
  IJobsServiceApi,
  IJobStatusResponse,
  InvoiceByAccountWithFiltersConsolidationRequest,
  InvoiceByAccountWithNumbersConsolidationRequest,
  InvoiceByFiltersConsolidationRequest,
  InvoiceByNumbersConsolidationRequest,
  IReturnsExcelDownloadJobRegistrationRequest,
  IReturnSummaryExcelDownloadJobRegistrationRequest,
  IStatusRequest,
  OrdersByLineItemDownloadRequest,
  OrdersByLineItemDownloadWithFiltersInvokeRequest,
} from "./IJobsService";

const JobsServiceApi: IJobsServiceApi = {
  async invokeInvoiceByAccountWithNumbersDownload(
    // Multiple Accounts, Invoices individually selected (CPM only)
    url: string,
    request: InvoiceByAccountWithNumbersConsolidationRequest,
    token: string,
    xid: string,
    location: string,
    lang?: string,
    enableInvoicesViewed?: boolean,
    mru?: string,
    format?: "zip" | "pdf"
  ): Promise<AxiosResponse<IJobRegistrationResponse>> {
    return consolidateInvoices(
      `${url}/jobs/v1/register/invoice-by-account-with-numbers-download?enableInvoicesRead=${enableInvoicesViewed}`,
      request,
      token,
      "Consolidated_Invoices",
      xid,
      location,
      INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION,
      format || "zip", // default to zip for multiple accounts,
      lang,
      mru
    );
  },
  async invokeInvoiceByAccountWithFilterDownload(
    // Multiple Accounts, Invoice selected via filter (CPM only)
    url: string,
    request: InvoiceByAccountWithFiltersConsolidationRequest,
    token: string,
    xid: string,
    location: string,
    lang?: string,
    enableInvoicesViewed?: boolean,
    mru?: string,
    format?: "zip" | "pdf"
  ): Promise<AxiosResponse<IJobRegistrationResponse>> {
    return consolidateInvoices(
      `${url}/jobs/v1/register/invoice-by-account-with-filters-download`,
      {
        ...request,
        enableInvoicesRead: enableInvoicesViewed,
      },
      token,
      "Consolidated_Invoices",
      xid,
      location,
      INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION,
      format || "zip", // default to zip for multiple accounts
      lang,
      mru
    );
  },
  invokeInvoiceByFilterDownload(
    url: string,
    request: InvoiceByFiltersConsolidationRequest,
    token: string,
    xid: string,
    location: string,
    lang: string | undefined,
    enableInvoicesViewed?: boolean,
    mru?: string
  ): Promise<AxiosResponse<IJobRegistrationResponse>> {
    return consolidateInvoices(
      `${url}/jobs/v1/register/invoices-by-filter-download?enableInvoicesRead=${enableInvoicesViewed}`,
      request,
      token,
      `${request.accountId}_CONSOLIDATED_INVOICES`,
      xid,
      location,
      INVOICE_FILTER,
      "pdf", // single account, always pdf
      lang,
      mru
    );
  },
  invokeInvoiceByNumbersConsolidationDownload(
    url: string,
    request: InvoiceByNumbersConsolidationRequest,
    token: string,
    xid: string,
    location: string,
    lang?: string,
    enableInvoicesViewed?: boolean,
    mru?: string
  ): Promise<AxiosResponse<IJobRegistrationResponse>> {
    return consolidateInvoices(
      `${url}/jobs/v1/register/invoice-by-numbers-consolidation?enableInvoicesRead=${enableInvoicesViewed}`,
      request,
      token,
      `${request.accountId}_CONSOLIDATED_INVOICES`,
      xid,
      location,
      INVOICE_CONSOLIDATION,
      "pdf", // single account, always pdf
      lang,
      mru
    );
  },
  getStatuses(
    jobsServiceUrl: string,
    request: IStatusRequest,
    accountId: string,
    token: string
  ): Promise<IJobStatusResponse> {
    return axios
      .postWithRetries(`${jobsServiceUrl}/jobs/v1/statuses`, request, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          accountId,
        },
      })
      .then((response: any) => response.data);
  },

  invokeInvoiceExcelDownload: async (
    jobsServiceUrl: string,
    request: IInvoiceExcelDownloadJobRegistrationRequest,
    token: string,
    lang?: string
  ): Promise<AxiosResponse<IJobRegistrationResponse>> =>
    axios
      .postWithRetries(
        `${jobsServiceUrl}/jobs/v1/register/invoice-excel-download`,
        {
          accountId: request.accountId,
          withHandlingUnits: request.withHandlingUnits,
          withOfficialDocumentNumbers: request.withOfficialDocumentNumbers,
          dealerLocation: request.dealerLocation,
          withLeadingZeros: request.withLeadingZeros,
          lang,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => ({
        ...response,
        data: {
          ...response.data,
          accountId: request.accountId,
          type: INVOICE_EXCEL,
        },
      })),

  invokeInvoiceExcelDownloadV2: async (
    jobsServiceUrl: string,
    request: IInvoiceExcelDownloadJobRegistrationRequestV2,
    token: string,
    lang?: string
  ): Promise<AxiosResponse<IJobRegistrationResponse>> =>
    axios
      .postWithRetries(
        `${jobsServiceUrl}/jobs/v2/register/invoice-excel-download`,
        {
          accountIds: request.accountIds,
          withHandlingUnits: request.withHandlingUnits,
          withOfficialDocumentNumbers: request.withOfficialDocumentNumbers,
          dealerLocation: request.dealerLocation,
          withLeadingZeros: request.withLeadingZeros,
          lang,
          invoiceNumber: request.invoiceNumber,
          invoiceType: request.invoiceType,
          srcProcessType: request.srcProcessType,
          nfeNumber: request.nfeNumber,
          documentNumber: request.documentNumber,
          huNumber: request.huNumber,
          dealerReference: request.dealerReference,
          officialDocumentNumber: request.officialDocumentNumber,
          partNumber: request.partNumber,
          startDate: request.startDate,
          endDate: request.endDate,
          dealerId: request.dealerId,
          factura: request.factura,
          sortField: request.sortField,
          sortDir: request.sortDir,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => ({
        ...response,
        data: {
          ...response.data,
          accountIds: request.accountIds,
          type: INVOICE_EXCEL,
        },
      })),

  downloadJob: async (
    jobsServiceUrl: string,
    jobId: string,
    accountId: string,
    filename: string,
    token: string,
    lang?: string
  ): Promise<AxiosResponse<Blob>> =>
    axios
      .getWithRetries(`${jobsServiceUrl}/jobs/v1/${jobId}/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
        params: { accountId, filename, lang },
      })
      .then((response) => response),

  async invokeAllOrdersExcelDownload(
    jobsServiceUrl: string,
    request: AllOrdersExcelDownloadInvokeRequest,
    token: string
  ): Promise<{ jobId: string }> {
    return axios
      .postWithRetries(
        `${jobsServiceUrl}/jobs/v1/register/order-excel-download`,
        request,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => response.data);
  },

  async invokeAllOrdersExcelDownloadWithFilters(
    jobsServiceUrl: string,
    request: AllOrdersExcelDownloadWithFiltersInvokeRequest,
    token: string
  ): Promise<{ jobId: string }> {
    return axios
      .postWithRetries(
        `${jobsServiceUrl}/jobs/v1/register/order-excel-download`,
        request,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => response.data);
  },

  async invokeOrdersByLineItemDownload(
    jobsServiceUrl: string,
    request: OrdersByLineItemDownloadRequest,
    token: string
  ): Promise<{ jobId: string }> {
    return axios
      .postWithRetries(
        `${jobsServiceUrl}/jobs/v1/register/orders-line-item-excel-download`,
        request,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => response.data);
  },

  async invokeOrdersByLineItemDownloadWithFilters(
    jobsServiceUrl: string,
    request: OrdersByLineItemDownloadWithFiltersInvokeRequest,
    token: string
  ): Promise<{ jobId: string }> {
    return axios
      .postWithRetries(
        `${jobsServiceUrl}/jobs/v1/register/orders-line-item-excel-download`,
        request,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => response.data);
  },

  downloadAllOrdersAndOrderByLineItemExcel: async (
    jobsServiceUrl: string,
    jobId: string,
    accountId: string,
    filename: string,
    token: string,
    lang?: string
  ): Promise<AxiosResponse<Blob>> =>
    axios
      .getWithRetries(`${jobsServiceUrl}/jobs/v1/${jobId}/order-download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
        params: { accountId, filename, lang },
      })
      .then((response) => response),

  invokeReturnsExcelDownload: async (
    jobsServiceUrl: string,
    request: IReturnsExcelDownloadJobRegistrationRequest,
    token: string,
    lang?: string
  ): Promise<AxiosResponse<IJobRegistrationResponse>> =>
    axios
      .postWithRetries(
        `${jobsServiceUrl}/jobs/v1/register/returns-excel-download`,
        { lang, criteria: request },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => ({
        ...response,
        data: {
          ...response.data,
          accountIds: request.accountIds,
          type: RETURNS_EXCEL,
        },
      })),

  invokeReturnSummaryExcelDownload: async (
    jobsServiceUrl: string,
    request: IReturnSummaryExcelDownloadJobRegistrationRequest,
    token: string,
    lang?: string
  ): Promise<AxiosResponse<IJobRegistrationResponse>> =>
    axios
      .postWithRetries(
        `${jobsServiceUrl}/jobs/v1/register/return-summary-excel-download`,
        { lang, ...request },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => ({
        ...response,
        data: {
          ...response.data,
          accountId: request.accountId,
          type: RETURN_SUMMARY_EXCEL,
        },
      })),
};

const consolidateInvoices = (
  url: string,
  request: any,
  token: string,
  filename: string,
  xid: string,
  location: string,
  type: string,
  format: "zip" | "pdf",
  lang?: string,
  mru?: string
) =>
  axios
    .postWithRetries(
      url,
      {
        filename,
        format,
        ...request,
        xid,
        location,
        mru,
        lang,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { lang },
      }
    )
    .then((response) => {
      const accountIdData = request.accountId
        ? { accountId: request.accountId }
        : { accountId: ALL_ACCOUNTS };

      return {
        ...response,
        data: {
          ...response.data,
          ...accountIdData,
          type,
        },
      };
    });

export default JobsServiceApi;
